'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useQuery, gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/free-mode';
import ProductCardHome from '../products/ProductCardHome';
import { GET_BEST_SELLERS, GET_HANDCRAFTED_BANNER } from '@/graphql/queries';
import { AWS_URL } from '@/constants';
import defaultImage from '../../images/default.jpg';



/* helpers */
const getProductUrl = (data) => {
  if (data && data.length) {
    const media = data[0]?.media;
    if (media?.path) return AWS_URL && media?.path ? `${AWS_URL}${media.path}` : defaultImage;
  }
  return defaultImage;
};

const buildMediaUrl = (fileObj) => {
  if (!fileObj || !fileObj.path) return null;
  const bucket = fileObj.bucket || 'jewel-ecom';
  const region = 'ap-south-1';
  return `https://${bucket}.s3.${region}.amazonaws.com/${fileObj.path}`;
};

function mapProductToCard(product, currencySymbol, conversionRate, isINR, selectedCurrency) {
  const images =
    Array.isArray(product.product_media) && product.product_media.length > 0
      ? [getProductUrl(product.product_media)]
      : [getProductUrl(product?.product_variations?.[0]?.product_media)];

  const totalStock =
    product.product_variations?.reduce((sum, variation) => {
      const variantStock =
        variation.product_variant_sizes?.reduce((varSum, size) => varSum + (size.quantity || 0), 0) ?? 0;
      return sum + variantStock;
    }, 0) ?? product.available_quantity ?? 0;

  const convertPrice = (price) => {
    if (!price) return null;
    return isINR ? parseFloat(price.toFixed(2)) : parseFloat((price / conversionRate).toFixed(2));
  };

  return {
    id: product.id,
    title: product.name,
    name: product.name,
    category: product.category?.name || '',
    category_id: product.category_id,
    price: convertPrice(product.price),
    price_per_gm: convertPrice(product.price_per_gm),
    images,
    href: `/product-detail/${product.id}`,
    rating: 4,
    dim_weight: product.dim_weight,
    dim_length: product.dim_length,
    dim_width: product.dim_width,
    product_code: product.product_code,
    description: product.description,
    sub_category: product.sub_category,
    min_order_quantity: product.min_order_quantity,
    product_media: product.product_media,
    product_total_stock: totalStock,
    currencySymbol: isINR
      ? currencySymbol
      : `${selectedCurrency?.name?.toUpperCase() || ''} ${currencySymbol}`.trim(),
    conversionRate,
    status: product.status,
    has_variation: product.has_variation,
    product_variations: product.product_variations,
  };
}

/* Best Sellers sub-component */
function BestSellers() {
  const router = useRouter();
  const swiperRef = useRef(null);

  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
  const conversionRate = selectedCurrency?.conversion_rate || 1;
  const currencySymbol = selectedCurrency?.symbol || '₹';
  const isINR =
    selectedCurrency?.name?.toLowerCase() === 'inr' ||
    selectedCurrency?.code?.toLowerCase() === 'inr';

  const { data, loading, error } = useQuery(GET_BEST_SELLERS, {
    variables: { where: {} },
  });

  useEffect(() => {
    router.prefetch('/shop/best-sellers');
  }, [router]);

  const handleMouseEnter = useCallback(() => {
    router.prefetch('/shop/best-sellers');
  }, [router]);

  const products = useMemo(() => {
    if (!data?.product_best_selling) return [];
    return data.product_best_selling
      .slice(0, 6)
      .map(function (item) {
        return mapProductToCard(item.product, currencySymbol, conversionRate, isINR, selectedCurrency);
      });
  }, [data, currencySymbol, conversionRate, isINR, selectedCurrency]);

  if (loading || error || products.length === 0) return null;

  return (
    <section className="w-full py-6 md:py-8">

      {/* Header */}
      <div className="w-full px-4 md:px-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4 md:mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="block h-px w-8 bg-[#bf875d]" aria-hidden="true" />
              <span
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-[10px] font-semibold tracking-[0.3em] text-[#bf875d] uppercase"
              >
                Curated For You
              </span>
            </div>
            <h2
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              className="text-3xl md:text-4xl font-light text-[#1a1a1a] leading-tight"
            >
              Best <em className="italic text-[#bf875d]">Sellers</em>
            </h2>
          </div>

          {/* Prev / Next arrows */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => swiperRef.current?.slidePrev()}
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#d4b896] text-[#bf875d] transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white hover:shadow-lg hover:shadow-[#bf875d]/30"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            </button>
            <button
              onClick={() => swiperRef.current?.slideNext()}
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#d4b896] text-[#bf875d] transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white hover:shadow-lg hover:shadow-[#bf875d]/30"
            >
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* Gold rule */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#d4b896] to-transparent" />
          <div className="w-1.5 h-1.5 bg-[#bf875d] rotate-45 flex-shrink-0" />
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#d4b896] to-transparent" />
        </div>
      </div>

      {/* Swiper */}
      <div className="w-full pl-4 md:pl-5">
        <Swiper
          modules={[FreeMode]}
          freeMode={true}
          grabCursor={true}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          slidesPerView={1.5}
          spaceBetween={16}
          breakpoints={{
            480:  { slidesPerView: 2.2, spaceBetween: 16 },
            640:  { slidesPerView: 3,   spaceBetween: 20 },
            768:  { slidesPerView: 3.5, spaceBetween: 20 },
            1024: { slidesPerView: 4.5, spaceBetween: 24 },
            1280: { slidesPerView: 5.2, spaceBetween: 28 },
          }}
        >
          {products.map(function (product) {
            return (
              <SwiperSlide key={product.id}>
                <ProductCardHome product={product} />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {/* Bottom CTA */}
      <div className="w-full px-4 md:px-5 mt-5 flex justify-center">
        <Link
          href="/shop/best-sellers"
          prefetch={true}
          onMouseEnter={handleMouseEnter}
          className="group inline-flex items-center gap-2 border border-[#d4b896] text-[#64483c] text-xs font-medium tracking-widest uppercase px-6 py-3 rounded-full transition-all duration-300 hover:bg-[#bf875d] hover:border-[#bf875d] hover:text-white"
        >
          View All Best Sellers
        </Link>
      </div>

    </section>
  );
}

/* Handcrafted Banner Component */
function HandcraftedBanner() {
  const { data, loading } = useQuery(GET_HANDCRAFTED_BANNER, {
    variables: { where: { type: { _eq: 'HANDCRAFTED_BANNER' } } },
    fetchPolicy: 'cache-and-network',
  });

  const [desktopBannerUrl, setDesktopBannerUrl] = useState(null);
  const [mobileBannerUrl, setMobileBannerUrl] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState('/shop?handcrafted=true');

  useEffect(() => {
    if (data?.tenant_org_web_configuration?.[0]) {
      const metas = data.tenant_org_web_configuration[0].web_configuration_meta || [];
      
      if (metas.length > 0) {
        const bannerMeta = metas[0]; // First banner (sorted by priority)
        
        // Build desktop banner URL from file object
        if (bannerMeta.file) {
          setDesktopBannerUrl(buildMediaUrl(bannerMeta.file));
        }
        
        // Build mobile banner URL from mobile_file object
        if (bannerMeta.mobile_file) {
          setMobileBannerUrl(buildMediaUrl(bannerMeta.mobile_file));
        }
        
        // Set redirect URL if available
        if (bannerMeta.redirection_url) {
          setRedirectUrl(bannerMeta.redirection_url);
        }
      }
    }
  }, [data]);

  // Show nothing while loading or if no banners found
  if (loading || (!desktopBannerUrl && !mobileBannerUrl)) {
    return null;
  }

  return (
    <div className="w-full py-6 md:py-12 md:my-20">
      <Link href={redirectUrl} className="block">
        <div className="relative w-full overflow-hidden shadow-2xl">
          {/* Mobile */}
          {mobileBannerUrl && (
            <div className="block md:hidden" style={{ aspectRatio: '1128 / 830' }}>
              <Image
                src={mobileBannerUrl}
                alt="Handcrafted Collection"
                fill
                className="object-contain"
                priority
              />
            </div>
          )}

          {/* Desktop */}
          {desktopBannerUrl && (
            <div className="hidden md:block" style={{ height: '600px' }}>
              <Image
                src={desktopBannerUrl}
                alt="Handcrafted Collection"
                fill
                className="object-cover"
                priority
              />
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

/* Main export */
export default function ProductCategoriesLayout2() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
      `}</style>

      {/* Handcrafted Banner */}
      <HandcraftedBanner />

      {/* Best Sellers */}
      <BestSellers />
    </>
  );
}